import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Search, Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CategoriesPage = () => {
  const { language } = useLanguage();
  const T = (fr, en) => (language === 'fr' ? fr : en);

  const [allCats, setAllCats] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const r = await axios.get(`${API}/categories`);
        setAllCats(r.data);
      } catch (_) {
        /* silent */
      }
    };
    fetch();
    window.scrollTo(0, 0);
  }, []);

  const localized = (c) => (language === 'fr' ? c.name_fr : c.name_en) || c.name;

  const topCats = useMemo(() => allCats.filter((c) => !c.parent_id), [allCats]);
  const subsByParent = useMemo(() => {
    const map = {};
    allCats.forEach((c) => {
      if (c.parent_id) {
        (map[c.parent_id] = map[c.parent_id] || []).push(c);
      }
    });
    return map;
  }, [allCats]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return topCats;
    return topCats.filter((c) =>
      [c.name, c.name_fr, c.name_en, c.slug].filter(Boolean).some((s) => s.toLowerCase().includes(q))
    );
  }, [topCats, search]);

  return (
    <div className="min-h-screen bg-[#FBF8F3]" data-testid="categories-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0F4C5C] via-[#0F4C5C] to-[#0B3A47] text-white py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 25% 30%, #E9C46A 0%, transparent 40%), radial-gradient(circle at 80% 70%, #2A9D8F 0%, transparent 40%)',
        }} />
        <div className="container-main relative">
          <p className="text-xs uppercase tracking-[0.4em] text-[#E9C46A] font-semibold mb-4">
            {T('Marketplace responsable', 'Responsible marketplace')}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
            {T('Toutes les catégories', 'All categories')}
            <span className="text-[#E9C46A]">.</span>
          </h1>
          <p className="text-lg text-white/75 max-w-2xl mb-8">
            {T(
              'Explorez plus de 30 univers — de l\'électronique à l\'épicerie fine, en passant par la mode, la maison et la beauté. Tous nos vendeurs sont vérifiés.',
              'Explore over 30 categories — from electronics to gourmet groceries, fashion, home and beauty. All our vendors are verified.'
            )}
          </p>
          <div className="relative max-w-lg">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#0F4C5C]/50" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={T('Rechercher une catégorie…', 'Search a category…')}
              className="h-13 pl-12 pr-4 rounded-full bg-white text-[#0F4C5C] placeholder:text-[#0F4C5C]/40 border-0 shadow-xl"
              data-testid="categories-search"
            />
          </div>
          <div className="flex items-center gap-2 mt-5 text-sm text-white/70">
            <Sparkles className="w-4 h-4 text-[#E9C46A]" />
            <span>{topCats.length} {T('catégories principales', 'main categories')} · {Object.values(subsByParent).reduce((acc, arr) => acc + arr.length, 0)} {T('sous-catégories', 'subcategories')}</span>
          </div>
        </div>
      </section>

      {/* Mosaic */}
      <section className="container-main py-16 md:py-20">
        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[#0F4C5C]/60">{T('Aucune catégorie ne correspond à votre recherche.', 'No category matches your search.')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((cat, idx) => {
              const subs = subsByParent[cat.id] || [];
              // Vary tile sizes for a richer mosaic feel
              const featured = idx % 9 === 0;
              return (
                <Link
                  key={cat.id}
                  to={`/shop?category=${cat.slug}`}
                  className={`group relative overflow-hidden rounded-2xl border border-[#0F4C5C]/10 bg-white hover:border-[#E9C46A]/40 transition-all duration-500 ${
                    featured ? 'sm:col-span-2 sm:row-span-1 lg:row-span-2' : ''
                  }`}
                  style={{ minHeight: featured ? '320px' : '220px' }}
                  data-testid={`category-tile-${cat.slug}`}
                >
                  {/* Background image */}
                  {cat.image_url && (
                    <div className="absolute inset-0 transition-transform duration-700 group-hover:scale-110">
                      <img
                        src={cat.image_url}
                        alt={localized(cat)}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F4C5C]/95 via-[#0F4C5C]/60 to-[#0F4C5C]/20 group-hover:from-[#0F4C5C] group-hover:via-[#0F4C5C]/70 transition-all duration-500" />

                  {/* Content */}
                  <div className="relative h-full flex flex-col justify-end p-6">
                    <h3 className={`font-bold text-white tracking-tight mb-1.5 ${featured ? 'text-3xl' : 'text-xl'}`}>
                      {localized(cat)}
                    </h3>
                    {subs.length > 0 && (
                      <p className="text-sm text-white/75 line-clamp-2 mb-3">
                        {subs.slice(0, 4).map((s) => localized(s)).join(' · ')}
                        {subs.length > 4 && ` · +${subs.length - 4}`}
                      </p>
                    )}
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#E9C46A] group-hover:gap-3 transition-all">
                      {T('Découvrir', 'Explore')}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>

                  {/* Subcategory pill count */}
                  {subs.length > 0 && (
                    <div className="absolute top-4 right-4 backdrop-blur-md bg-white/15 border border-white/25 rounded-full px-3 py-1 text-xs font-semibold text-white">
                      {subs.length} {T('sous-cat.', 'subs.')}
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </section>

      {/* Bottom CTA */}
      <section className="container-main pb-20">
        <div className="bg-gradient-to-br from-[#0F4C5C] to-[#0B3A47] rounded-3xl p-10 md:p-14 text-center text-white relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#E9C46A]/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-[#2A9D8F]/15 blur-3xl" />
          <div className="relative">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">
              {T('Vous êtes vendeur ?', 'Are you a seller?')}
            </h2>
            <p className="text-white/75 max-w-xl mx-auto mb-7">
              {T(
                'Rejoignez notre marketplace et touchez des milliers d\'acheteurs vérifiés. Inscription gratuite, commission claire de 10%.',
                'Join our marketplace and reach thousands of verified buyers. Free signup, transparent 10% commission.'
              )}
            </p>
            <Link
              to="/become-seller"
              className="inline-flex items-center gap-2 bg-[#E9C46A] hover:bg-[#E9C46A]/90 text-[#0F4C5C] font-bold px-7 py-3.5 rounded-full transition-all hover:gap-3"
            >
              {T('Devenir vendeur', 'Become a seller')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
